/** biome-ignore-all lint/correctness/noUnusedImports: reason */

import React, { createContext, useContext, useEffect, useState } from "react";

import { debugError, debugLog } from "../../helpers/debug";
import { useAuth } from "../auth";
import { useFirebase } from "../Firebase";
import { transformCollectionSnap } from "../helpers";
import { formatUserData } from "./formatting";

const UsersContext = createContext({});

export const UsersProvider = ({ children }) => {
  const firebase = useFirebase();
  const { claims } = useAuth();

  const [users, setUsers] = useState();

  useEffect(() => {
    debugLog("[UsersProvider] Setting up users listener:", {
      companyLink: claims?.companyLink,
      hasCompanyLink: !!claims?.companyLink,
    });

    return claims?.companyLink
      ? firebase.users().onSnapshot(
          snap => {
            const companyUsers = transformCollectionSnap(snap).filter(
              user => user.companyLink === claims.companyLink
            );

            debugLog("[UsersProvider] Users snapshot received:", {
              companyUsers: companyUsers.length,
              totalUsers: snap.size,
            });

            setUsers(companyUsers);
          },
          err => {
            debugError("[UsersProvider] Users snapshot error:", err);
            debugLog(err);
          }
        )
      : () => {
          debugLog("[UsersProvider] No companyLink, skipping users listener");
        };
  }, [
    claims.companyLink,
    firebase,
  ]);

  const createUser = async user => {
    if (!claims?.companyLink) {
      debugError("[UsersProvider] Cannot create user - no companyLink");
      return;
    }

    return firebase.users().add(
      formatUserData({
        ...user,
        dateCreated: new Date(),
      })
    );
  };

  const updateUser = async (user, id) => {
    if (!claims?.companyLink) {
      debugError("[UsersProvider] Cannot update user - no companyLink");
      return;
    }

    return firebase
      .users()
      .doc(id)
      .update(
        formatUserData({
          ...user,
          dateUpdated: new Date(),
        })
      );
  };

  const archiveUser = async (isArchive, id) => {
    if (!claims?.companyLink) {
      debugError("[UsersProvider] Cannot archive user - no companyLink");
      return;
    }

    return firebase.users().doc(id).update({
      dateUpdated: new Date(),
      isArchive: !isArchive,
    });
  };

  return (
    <UsersContext.Provider
      value={{
        archiveUser,
        createUser,
        updateUser,
        users,
      }}
    >
      {children}
    </UsersContext.Provider>
  );
};

export const UsersConsumer = UsersContext.Consumer;

export const useUsers = () => useContext(UsersContext);
